import type {
  OperatingStatus,
  PriceLevel,
  SearchFilters,
} from "./types";
import {
  createPackDefaultFilters,
  DEFAULT_AMENITY_PACK_ID,
} from "./amenityPacks";

export const METERS_PER_MILE = 1609.344;
export const DEFAULT_RADIUS_METERS = METERS_PER_MILE;
export const MIN_RADIUS_METERS = METERS_PER_MILE / 4;
export const MAX_RADIUS_METERS = METERS_PER_MILE * 3;
export const RADIUS_STEP_METERS = METERS_PER_MILE / 4;
export const MIN_GRID_DIMENSION = 4;
export const MEDIUM_GRID_DIMENSION = 6;
export const MAX_GRID_DIMENSION = 8;
export const MAX_DETAIL_PLACE_IDS = 20;
export const MAX_AGGREGATE_PLACE_IDS = 100;
export const defaultFilters: SearchFilters = createPackDefaultFilters(DEFAULT_AMENITY_PACK_ID);

export const priceLevelOptions: Array<{ value: PriceLevel; label: string }> = [
  {
    value: "PRICE_LEVEL_FREE",
    label: "Free",
  },
  {
    value: "PRICE_LEVEL_INEXPENSIVE",
    label: "$",
  },
  {
    value: "PRICE_LEVEL_MODERATE",
    label: "$$",
  },
  {
    value: "PRICE_LEVEL_EXPENSIVE",
    label: "$$$",
  },
  {
    value: "PRICE_LEVEL_VERY_EXPENSIVE",
    label: "$$$$",
  },
];

function unique<T>(items: T[]): T[] {
  return [...new Set(items)];
}

export function normalizeSearchFilters(filters: SearchFilters): SearchFilters {
  const includedTypes = unique(filters.includedTypes.filter(Boolean));
  const operatingStatus: OperatingStatus[] = ["OPERATING_STATUS_OPERATIONAL"];
  const priceLevels =
    filters.priceLevels && filters.priceLevels.length > 0
      ? unique(filters.priceLevels)
      : undefined;
  const minRating =
    typeof filters.minRating === "number" && Number.isFinite(filters.minRating)
      ? Math.min(5, Math.max(1, Number(filters.minRating.toFixed(1))))
      : undefined;

  return {
    includedTypes,
    operatingStatus,
    priceLevels,
    minRating,
  };
}

export function formatDistance(radiusMeters: number): string {
  const miles = radiusMeters / METERS_PER_MILE;

  if (miles < 1) {
    return `${Number(miles.toFixed(2)).toString()} mi`;
  }

  return `${Number(miles.toFixed(1)).toString()} mi`;
}

export function getGridDimensionsForZoom(zoom?: number): { rows: number; cols: number } {
  if (typeof zoom !== "number" || !Number.isFinite(zoom)) {
    return {
      rows: MEDIUM_GRID_DIMENSION,
      cols: MEDIUM_GRID_DIMENSION,
    };
  }

  if (zoom >= 14) {
    return {
      rows: MAX_GRID_DIMENSION,
      cols: MAX_GRID_DIMENSION,
    };
  }

  if (zoom >= 11) {
    return {
      rows: MEDIUM_GRID_DIMENSION,
      cols: MEDIUM_GRID_DIMENSION,
    };
  }

  return {
    rows: MIN_GRID_DIMENSION,
    cols: MIN_GRID_DIMENSION,
  };
}
